<template>
  <div class="sales-page">
    <!-- 顶部快捷操作栏 -->
    <div class="action-bar">
      <div class="action-group">
        <button class="action-btn btn-primary" @click="showAddModal = true">
          <span class="btn-icon">+</span>
          <span>新增商机</span>
        </button>
        <button class="action-btn btn-secondary" @click="handleImport">
          <span class="btn-icon">↑</span>
          <span>导入线索</span>
        </button>
      </div>
      <div class="filter-group">
        <div class="filter-item">
          <span class="filter-label">阶段</span>
          <select v-model="filterStage" class="filter-select">
            <option value="">全部阶段</option>
            <option value="lead">初步接触</option>
            <option value="qualified">需求确认</option>
            <option value="proposal">方案报价</option>
            <option value="negotiation">商务谈判</option>
            <option value="closed">成交签约</option>
          </select>
        </div>
        <div class="filter-item">
          <span class="filter-label">负责人</span>
          <select v-model="filterOwner" class="filter-select">
            <option value="">全部人员</option>
            <option v-for="p in salesPeople" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>
        <div class="search-box">
          <input 
            v-model="searchKeyword" 
            type="text" 
            placeholder="搜索客户名称..." 
            class="search-input"
          />
          <span class="search-icon">🔍</span>
        </div>
      </div>
    </div>

    <!-- 销售漏斗看板 -->
    <div class="funnel-board">
      <div 
        class="funnel-stage" 
        v-for="(stage, index) in funnelStages" 
        :key="stage.key"
        :class="{ active: selectedStage === stage.key }"
        @click="selectedStage = selectedStage === stage.key ? '' : stage.key"
      >
        <div class="stage-header">
          <div class="stage-dot" :style="{ background: stage.color }"></div>
          <span class="stage-name">{{ stage.name }}</span>
          <span class="stage-count">{{ stage.count }}</span>
        </div>
        <div class="stage-amount">¥{{ stage.amount }}万</div>
        <div class="stage-bar">
          <div class="stage-bar-fill" :style="{ 
            width: stage.count / maxStageCount * 100 + '%',
            background: stage.color 
          }"></div>
        </div>
        <div class="stage-conversion" v-if="index < funnelStages.length - 1">
          转化率 {{ stage.conversion }}%
        </div>
      </div>
    </div>

    <!-- 主体内容区：左侧商机列表 + 右侧详情 -->
    <div class="main-content">
      <!-- 商机列表 -->
      <div class="opportunity-list" :class="{ 'detail-open': selectedOpportunity }">
        <div class="list-header">
          <span class="list-title">商机列表</span>
          <span class="list-count">共 {{ filteredOpportunities.length }} 条</span>
        </div>
        <div class="list-body">
          <div 
            class="opportunity-item" 
            v-for="item in filteredOpportunities" 
            :key="item.id"
            :class="{ active: selectedOpportunity && selectedOpportunity.id === item.id }"
            @click="selectOpportunity(item)"
          >
            <div class="item-top">
              <div class="item-company">{{ item.company }}</div>
              <div class="item-amount">¥{{ item.amount }}万</div>
            </div>
            <div class="item-mid">
              <span class="item-contact">👤 {{ item.contact }}</span>
              <span class="item-stage" :style="{ 
                background: getStageColor(item.stage) + '20',
                color: getStageColor(item.stage) 
              }">{{ getStageName(item.stage) }}</span>
            </div>
            <div class="item-bottom">
              <span class="item-owner">负责人：{{ item.owner }}</span>
              <span class="item-date">{{ item.updateTime }}</span>
            </div>
            <div class="item-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ 
                  width: item.probability + '%',
                  background: getStageColor(item.stage)
                }"></div>
              </div>
              <span class="progress-text">{{ item.probability }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 商机详情面板 -->
      <div class="detail-panel" v-if="selectedOpportunity">
        <div class="detail-header">
          <div class="detail-title">
            <h3>{{ selectedOpportunity.company }}</h3>
            <span class="detail-badge" :style="{ 
              background: getStageColor(selectedOpportunity.stage) + '20',
              color: getStageColor(selectedOpportunity.stage)
            }">{{ getStageName(selectedOpportunity.stage) }}</span>
          </div>
          <button class="detail-close" @click="selectedOpportunity = null">✕</button>
        </div>

        <div class="detail-body">
          <!-- 基本信息 -->
          <div class="detail-section">
            <div class="section-title">基本信息</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">预计金额</span>
                <span class="info-value highlight">¥{{ selectedOpportunity.amount }}万</span>
              </div>
              <div class="info-item">
                <span class="info-label">成交概率</span>
                <span class="info-value">{{ selectedOpportunity.probability }}%</span>
              </div>
              <div class="info-item">
                <span class="info-label">预计成交</span>
                <span class="info-value">{{ selectedOpportunity.expectedDate }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">负责人</span>
                <span class="info-value">{{ selectedOpportunity.owner }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">联系人</span>
                <span class="info-value">{{ selectedOpportunity.contact }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">联系电话</span>
                <span class="info-value">{{ selectedOpportunity.phone }}</span>
              </div>
            </div>
          </div>

          <!-- 跟进时间线 -->
          <div class="detail-section">
            <div class="section-title">跟进记录</div>
            <div class="timeline">
              <div class="timeline-item" v-for="(record, idx) in selectedOpportunity.records" :key="idx">
                <div class="timeline-dot" :class="{ latest: idx === 0 }"></div>
                <div class="timeline-content">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ record.action }}</span>
                    <span class="timeline-time">{{ record.time }}</span>
                  </div>
                  <div class="timeline-desc">{{ record.desc }}</div>
                  <div class="timeline-author">by {{ record.author }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 快捷操作 -->
          <div class="detail-actions">
            <button class="action-btn btn-primary" @click="handleFollowUp">
              <span>📝 写跟进</span>
            </button>
            <button class="action-btn btn-secondary" @click="handleEdit">
              <span>✏️ 编辑</span>
            </button>
            <button class="action-btn btn-warn" @click="handleTransfer">
              <span>↻ 转交</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增商机弹窗 -->
    <div class="modal-overlay" v-if="showAddModal" @click.self="showAddModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>新增商机</h3>
          <button class="modal-close" @click="showAddModal = false">✕</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-item">
              <label>客户名称 <span class="required">*</span></label>
              <input type="text" v-model="newOpportunity.company" placeholder="请输入客户名称" />
            </div>
            <div class="form-item">
              <label>预计金额（万）</label>
              <input type="number" v-model="newOpportunity.amount" placeholder="0.00" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>联系人</label>
              <input type="text" v-model="newOpportunity.contact" placeholder="请输入联系人姓名" />
            </div>
            <div class="form-item">
              <label>联系电话</label>
              <input type="text" v-model="newOpportunity.phone" placeholder="请输入联系电话" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>当前阶段</label>
              <select v-model="newOpportunity.stage">
                <option value="lead">初步接触</option>
                <option value="qualified">需求确认</option>
                <option value="proposal">方案报价</option>
                <option value="negotiation">商务谈判</option>
              </select>
            </div>
            <div class="form-item">
              <label>负责人</label>
              <select v-model="newOpportunity.owner">
                <option v-for="p in salesPeople" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
          </div>
          <div class="form-item full">
            <label>需求描述</label>
            <textarea v-model="newOpportunity.desc" rows="3" placeholder="请输入客户需求描述..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showAddModal = false">取消</button>
          <button class="btn-primary" @click="submitNewOpportunity">确认创建</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrmSales',
  data() {
    return {
      filterStage: '',
      filterOwner: '',
      searchKeyword: '',
      selectedStage: '',
      selectedOpportunity: null,
      showAddModal: false,
      salesPeople: ['李明', '王芳', '张伟', '刘洋', '陈静'],
      funnelStages: [
        { key: 'lead', name: '初步接触', count: 45, amount: 860, conversion: 78, color: '#4CAF50' },
        { key: 'qualified', name: '需求确认', count: 35, amount: 720, conversion: 65, color: '#66BB6A' },
        { key: 'proposal', name: '方案报价', count: 23, amount: 580, conversion: 52, color: '#81C784' },
        { key: 'negotiation', name: '商务谈判', count: 12, amount: 420, conversion: 85, color: '#A5D6A7' },
        { key: 'closed', name: '成交签约', count: 36, amount: 486.5, conversion: 100, color: '#2E7D32' }
      ],
      opportunities: [
        {
          id: 1,
          company: '华夏科技有限公司',
          amount: 120,
          stage: 'negotiation',
          probability: 75,
          contact: '张总',
          phone: '138****8888',
          owner: '李明',
          expectedDate: '2026-07-15',
          updateTime: '2小时前',
          records: [
            { action: '电话沟通', time: '2026-06-25 10:30', desc: '客户对方案表示认可，需要调整付款周期为3期', author: '李明' },
            { action: '方案提交', time: '2026-06-20 14:00', desc: '提交了定制化解决方案，包含详细报价单', author: '李明' },
            { action: '初次拜访', time: '2026-06-15 09:00', desc: '拜访客户了解需求，初步达成合作意向', author: '李明' }
          ]
        },
        {
          id: 2,
          company: '创新未来集团',
          amount: 85,
          stage: 'proposal',
          probability: 50,
          contact: '王经理',
          phone: '139****6666',
          owner: '王芳',
          expectedDate: '2026-08-01',
          updateTime: '5小时前',
          records: [
            { action: '需求调研', time: '2026-06-24 16:00', desc: '完成客户需求调研，准备方案设计', author: '王芳' },
            { action: '初次接触', time: '2026-06-20 11:00', desc: '通过展会获取线索，电话初步沟通', author: '王芳' }
          ]
        },
        {
          id: 3,
          company: '鼎盛实业股份',
          amount: 200,
          stage: 'qualified',
          probability: 35,
          contact: '李总监',
          phone: '137****9999',
          owner: '张伟',
          expectedDate: '2026-09-10',
          updateTime: '1天前',
          records: [
            { action: '需求确认', time: '2026-06-23 15:30', desc: '客户表达了明确的采购意向，预算200万左右', author: '张伟' }
          ]
        },
        {
          id: 4,
          company: '蓝海智能科技',
          amount: 65,
          stage: 'lead',
          probability: 15,
          contact: '赵主任',
          phone: '136****5555',
          owner: '刘洋',
          expectedDate: '2026-10-01',
          updateTime: '2天前',
          records: [
            { action: '线索录入', time: '2026-06-22 09:00', desc: '官网注册线索，暂未联系', author: '系统' }
          ]
        },
        {
          id: 5,
          company: '鹏程万里物流',
          amount: 150,
          stage: 'closed',
          probability: 100,
          contact: '陈总',
          phone: '135****7777',
          owner: '陈静',
          expectedDate: '2026-06-20',
          updateTime: '3天前',
          records: [
            { action: '合同签署', time: '2026-06-20 14:00', desc: '正式签署合同，首付款已到账', author: '陈静' },
            { action: '商务谈判', time: '2026-06-18 10:00', desc: '就价格和交付周期达成一致', author: '陈静' },
            { action: '方案演示', time: '2026-06-10 09:30', desc: '现场方案演示，客户满意度高', author: '陈静' }
          ]
        },
        {
          id: 6,
          company: '星辰大海传媒',
          amount: 45,
          stage: 'lead',
          probability: 10,
          contact: '孙经理',
          phone: '133****3333',
          owner: '李明',
          expectedDate: '2026-11-01',
          updateTime: '3天前',
          records: [
            { action: '线索录入', time: '2026-06-22 11:00', desc: '展会名片录入，待跟进', author: '系统' }
          ]
        },
        {
          id: 7,
          company: '金石建筑工程',
          amount: 320,
          stage: 'negotiation',
          probability: 80,
          contact: '周董事长',
          phone: '131****1111',
          owner: '王芳',
          expectedDate: '2026-07-30',
          updateTime: '1天前',
          records: [
            { action: '高层会晤', time: '2026-06-24 14:00', desc: '与技术总监和采购总监进行深度交流', author: '王芳' },
            { action: '方案评审', time: '2026-06-18 09:00', desc: '客户内部方案评审通过', author: '王芳' }
          ]
        },
        {
          id: 8,
          company: '云端数据服务',
          amount: 90,
          stage: 'proposal',
          probability: 45,
          contact: '吴技术',
          phone: '132****2222',
          owner: '张伟',
          expectedDate: '2026-08-15',
          updateTime: '4小时前',
          records: [
            { action: '技术对接', time: '2026-06-25 09:00', desc: '技术团队对接完成，确认集成方案', author: '张伟' }
          ]
        }
      ],
      newOpportunity: {
        company: '',
        amount: '',
        contact: '',
        phone: '',
        stage: 'lead',
        owner: '李明',
        desc: ''
      }
    }
  },
  computed: {
    maxStageCount() {
      return Math.max(...this.funnelStages.map(s => s.count))
    },
    filteredOpportunities() {
      let result = this.opportunities
      if (this.filterStage) {
        result = result.filter(item => item.stage === this.filterStage)
      }
      if (this.filterOwner) {
        result = result.filter(item => item.owner === this.filterOwner)
      }
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase()
        result = result.filter(item => 
          item.company.toLowerCase().includes(kw) ||
          item.contact.toLowerCase().includes(kw)
        )
      }
      if (this.selectedStage) {
        result = result.filter(item => item.stage === this.selectedStage)
      }
      return result
    }
  },
  methods: {
    getStageColor(stage) {
      const map = {
        lead: '#4CAF50',
        qualified: '#66BB6A',
        proposal: '#81C784',
        negotiation: '#A5D6A7',
        closed: '#2E7D32'
      }
      return map[stage] || '#4CAF50'
    },
    getStageName(stage) {
      const map = {
        lead: '初步接触',
        qualified: '需求确认',
        proposal: '方案报价',
        negotiation: '商务谈判',
        closed: '成交签约'
      }
      return map[stage] || stage
    },
    selectOpportunity(item) {
      this.selectedOpportunity = item
    },
    handleImport() {
      alert('导入功能开发中...')
    },
    handleFollowUp() {
      alert('写跟进功能开发中...')
    },
    handleEdit() {
      alert('编辑功能开发中...')
    },
    handleTransfer() {
      alert('转交功能开发中...')
    },
    submitNewOpportunity() {
      if (!this.newOpportunity.company) {
        alert('请输入客户名称')
        return
      }
      const newItem = {
        id: Date.now(),
        ...this.newOpportunity,
        amount: Number(this.newOpportunity.amount) || 0,
        probability: this.getDefaultProbability(this.newOpportunity.stage),
        expectedDate: '2026-12-31',
        updateTime: '刚刚',
        records: [
          { action: '线索录入', time: new Date().toLocaleString('zh-CN'), desc: '手动创建商机', author: '系统管理员' }
        ]
      }
      this.opportunities.unshift(newItem)
      this.showAddModal = false
      this.resetNewOpportunity()
    },
    getDefaultProbability(stage) {
      const map = { lead: 15, qualified: 35, proposal: 50, negotiation: 75 }
      return map[stage] || 15
    },
    resetNewOpportunity() {
      this.newOpportunity = {
        company: '',
        amount: '',
        contact: '',
        phone: '',
        stage: 'lead',
        owner: '李明',
        desc: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.sales-page {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  background: #0a1f0f;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.2);
    border-radius: 3px;
  }
}

// ===== 快捷操作栏 =====
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  .action-group {
    display: flex;
    gap: 10px;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;

  .btn-icon {
    font-size: 14px;
    font-weight: 600;
  }

  &.btn-primary {
    background: linear-gradient(135deg, #2E7D32, #1B5E20);
    color: #e8f5e9;
    box-shadow: 0 2px 10px rgba(46, 125, 50, 0.3);

    &:hover {
      background: linear-gradient(135deg, #388E3C, #2E7D32);
      box-shadow: 0 4px 16px rgba(46, 125, 50, 0.4);
      transform: translateY(-1px);
    }
  }

  &.btn-secondary {
    background: rgba(255, 255, 255, 0.05);
    color: #a5d6a7;
    border: 1px solid rgba(76, 175, 80, 0.2);

    &:hover {
      background: rgba(76, 175, 80, 0.1);
      border-color: rgba(76, 175, 80, 0.4);
    }
  }

  &.btn-warn {
    background: rgba(255, 152, 0, 0.1);
    color: #FFB74D;
    border: 1px solid rgba(255, 152, 0, 0.2);

    &:hover {
      background: rgba(255, 152, 0, 0.2);
    }
  }
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 6px;

  .filter-label {
    font-size: 12px;
    color: #66BB6A;
  }

  .filter-select {
    padding: 6px 10px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 13px;
    outline: none;
    cursor: pointer;

    &:focus {
      border-color: #4CAF50;
    }

    option {
      background: #102A15;
    }
  }
}

.search-box {
  position: relative;

  .search-input {
    width: 200px;
    padding: 7px 32px 7px 12px;
    background: rgba(16, 42, 21, 0.8);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #e8f5e9;
    font-size: 13px;
    outline: none;

    &::placeholder {
      color: #4CAF50;
      opacity: 0.5;
    }

    &:focus {
      border-color: #4CAF50;
    }
  }

  .search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    opacity: 0.6;
  }
}

// ===== 销售漏斗看板 =====
.funnel-board {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.funnel-stage {
  padding: 16px;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(76, 175, 80, 0.3);
    transform: translateY(-2px);
  }

  &.active {
    border-color: #4CAF50;
    box-shadow: 0 0 20px rgba(76, 175, 50, 0.15);
  }

  .stage-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;

    .stage-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    .stage-name {
      font-size: 13px;
      color: #a5d6a7;
      flex: 1;
    }

    .stage-count {
      font-size: 20px;
      font-weight: 700;
      color: #e8f5e9;
      font-family: 'Courier New', monospace;
    }
  }

  .stage-amount {
    font-size: 14px;
    color: #81C784;
    margin-bottom: 10px;
  }

  .stage-bar {
    height: 4px;
    background: rgba(76, 175, 80, 0.08);
    border-radius: 2px;
    overflow: hidden;

    .stage-bar-fill {
      height: 100%;
      border-radius: 2px;
      transition: width 0.5s ease;
    }
  }

  .stage-conversion {
    margin-top: 8px;
    font-size: 11px;
    color: #66BB6A;
    opacity: 0.7;
    text-align: right;
  }
}

// ===== 主体内容区 =====
.main-content {
  display: flex;
  gap: 16px;
  min-height: 500px;
}

// ===== 商机列表 =====
.opportunity-list {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: flex 0.3s ease;

  &.detail-open {
    flex: 0 0 45%;
  }

  .list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .list-title {
      font-size: 15px;
      font-weight: 600;
      color: #e8f5e9;
    }

    .list-count {
      font-size: 12px;
      color: #66BB6A;
      opacity: 0.7;
    }
  }

  .list-body {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

.opportunity-item {
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid transparent;

  &:hover {
    background: rgba(76, 175, 80, 0.06);
    border-color: rgba(76, 175, 80, 0.15);
  }

  &.active {
    background: rgba(76, 175, 80, 0.1);
    border-color: rgba(76, 175, 80, 0.3);
  }

  .item-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .item-company {
      font-size: 14px;
      font-weight: 600;
      color: #e8f5e9;
    }

    .item-amount {
      font-size: 14px;
      font-weight: 700;
      color: #81C784;
      font-family: 'Courier New', monospace;
    }
  }

  .item-mid {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    .item-contact {
      font-size: 12px;
      color: #66BB6A;
    }

    .item-stage {
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 500;
    }
  }

  .item-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .item-owner {
      font-size: 11px;
      color: #66BB6A;
      opacity: 0.8;
    }

    .item-date {
      font-size: 11px;
      color: #4CAF50;
      opacity: 0.6;
    }
  }

  .item-progress {
    display: flex;
    align-items: center;
    gap: 8px;

    .progress-bar {
      flex: 1;
      height: 4px;
      background: rgba(76, 175, 80, 0.08);
      border-radius: 2px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.5s ease;
      }
    }

    .progress-text {
      font-size: 11px;
      color: #66BB6A;
      width: 32px;
      text-align: right;
    }
  }
}

// ===== 详情面板 =====
.detail-panel {
  flex: 1;
  background: linear-gradient(135deg, rgba(16, 42, 21, 0.9) 0%, rgba(13, 31, 15, 0.95) 100%);
  border: 1px solid rgba(76, 175, 80, 0.12);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px;
    border-bottom: 1px solid rgba(76, 175, 80, 0.08);

    .detail-title {
      display: flex;
      align-items: center;
      gap: 10px;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .detail-badge {
        padding: 3px 10px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 500;
      }
    }

    .detail-close {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #a5d6a7;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(239, 83, 80, 0.15);
        border-color: rgba(239, 83, 80, 0.3);
        color: #EF5350;
      }
    }
  }

  .detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(76, 175, 80, 0.2);
      border-radius: 2px;
    }
  }
}

.detail-section {
  margin-bottom: 20px;

  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: #81C784;
    margin-bottom: 12px;
    padding-left: 10px;
    border-left: 3px solid #4CAF50;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .info-item {
    padding: 10px 12px;
    background: rgba(76, 175, 80, 0.04);
    border: 1px solid rgba(76, 175, 80, 0.08);
    border-radius: 8px;

    .info-label {
      display: block;
      font-size: 11px;
      color: #66BB6A;
      opacity: 0.8;
      margin-bottom: 4px;
    }

    .info-value {
      font-size: 14px;
      font-weight: 600;
      color: #e8f5e9;

      &.highlight {
        color: #81C784;
        font-size: 16px;
      }
    }
  }
}

// ===== 时间线 =====
.timeline {
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(76, 175, 80, 0.15);
  }
}

.timeline-item {
  position: relative;
  padding-bottom: 16px;

  &:last-child {
    padding-bottom: 0;
  }

  .timeline-dot {
    position: absolute;
    left: -17px;
    top: 4px;
    width: 9px;
    height: 9px;
    background: #4CAF50;
    border: 2px solid #0d1f0f;
    border-radius: 50%;

    &.latest {
      background: #81C784;
      box-shadow: 0 0 8px rgba(129, 199, 132, 0.5);
    }
  }

  .timeline-content {
    .timeline-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;

      .timeline-action {
        font-size: 13px;
        font-weight: 600;
        color: #e8f5e9;
      }

      .timeline-time {
        font-size: 11px;
        color: #66BB6A;
        opacity: 0.7;
      }
    }

    .timeline-desc {
      font-size: 12px;
      color: #a5d6a7;
      line-height: 1.6;
      margin-bottom: 4px;
    }

    .timeline-author {
      font-size: 11px;
      color: #66BB6A;
      opacity: 0.6;
    }
  }
}

// ===== 详情操作按钮 =====
.detail-actions {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid rgba(76, 175, 80, 0.08);

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}

// ===== 弹窗 =====
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content {
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  background: linear-gradient(135deg, #102A15 0%, #0d2311 100%);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: scaleIn 0.2s ease;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #e8f5e9;
  }

  .modal-close {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    color: #a5d6a7;
    font-size: 14px;
    cursor: pointer;

    &:hover {
      background: rgba(239, 83, 80, 0.15);
      border-color: rgba(239, 83, 80, 0.3);
      color: #EF5350;
    }
  }
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .form-item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &.full {
      grid-column: 1 / -1;
    }

    label {
      font-size: 12px;
      color: #81C784;

      .required {
        color: #EF5350;
      }
    }

    input, select, textarea {
      padding: 8px 12px;
      background: rgba(16, 42, 21, 0.8);
      border: 1px solid rgba(76, 175, 80, 0.2);
      border-radius: 6px;
      color: #e8f5e9;
      font-size: 13px;
      outline: none;

      &::placeholder {
        color: #4CAF50;
        opacity: 0.4;
      }

      &:focus {
        border-color: #4CAF50;
      }
    }

    textarea {
      resize: vertical;
      min-height: 80px;
      font-family: inherit;
    }

    option {
      background: #102A15;
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(76, 175, 80, 0.1);

  button {
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s;

    &.btn-secondary {
      background: rgba(255, 255, 255, 0.05);
      color: #a5d6a7;
      border: 1px solid rgba(76, 175, 80, 0.2);

      &:hover {
        background: rgba(76, 175, 80, 0.1);
      }
    }

    &.btn-primary {
      background: linear-gradient(135deg, #2E7D32, #1B5E20);
      color: #e8f5e9;

      &:hover {
        background: linear-gradient(135deg, #388E3C, #2E7D32);
      }
    }
  }
}

// ===== 响应式 =====
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
  }

  .opportunity-list.detail-open {
    flex: 1;
    max-height: 400px;
  }

  .detail-panel {
    min-height: 400px;
  }
}
</style>